import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { fromCartResponseToCart } from '@banyan/commons/dtos/quote.dto';
import { RequestsDto, RequestsDtoV2 } from '@banyan/commons/dtos/requests.dto';
import { ProductFeedback } from '@banyan/commons/models/product-feedback.model';
import { QuoteProposal } from '@banyan/commons/models/quote-proposal.model';
import { QuoteResponse } from '@banyan/commons/models/quote-response.model';
import { RequestDetails } from '@banyan/commons/models/request-details.model';
import { NewProposalNotification } from '@banyan/commons/models/request-notification.model';
import { RequestsResponse, RequestsResponseV2 } from '@banyan/commons/models/requests-response.model';
import { AccountService } from '@banyan/modules/account/services/account.service';
import { ProgressSpinnerService } from '@banyan/modules/shared/services/progress-spinner/progress-spinner.service';
import { AlertComponent } from '@banyan/modules/standalone/alert/alert.component';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class QuoteService {
  httpClient = inject(HttpClient);
  progressSpinnerService = inject(ProgressSpinnerService);
  accountService = inject(AccountService);
  router = inject(Router);
  private readonly PATH: string = '/request';
  private readonly PATH_V2: string = '/v2/requests';
  destroyRef = inject(DestroyRef);
  requests = signal<RequestsResponse | null>(null);
  requestsV2 = signal<RequestsResponseV2 | null>(null);
  expiringProposals = signal<RequestsResponse | null>(null);
  newProposals = signal<RequestsResponse | null>(null);
  expiringProposalsCount = signal<number | null>(null);
  newProposalsV2 = signal<NewProposalNotification | null>(null);
  quoteProposal = signal<QuoteProposal | null>(null);
  requestsLoaded = signal<boolean>(false);
  requestsV2Loaded = signal<boolean>(false);
  quoteLoaded = signal<boolean>(false);
  isLoadingRequest = signal<boolean>(false);
  request = signal<RequestDetails | null>(null);
  snackBar = inject(MatSnackBar);

  constructor() {
  }

  loadQuoteProposal(id: number): void {
    this.progressSpinnerService.show();
    this.quoteLoaded.set(false);
    this.httpClient.get<QuoteResponse>(
      `${environment.middlewareUrl}${this.PATH}/${id}`,
    ).subscribe({
      next: (data: any) => {
        this.quoteProposal.set(fromCartResponseToCart(data));
        this.quoteLoaded.set(true);
        this.progressSpinnerService.hide();
      },
      error: (error) => {
        this.quoteProposal.set(null);
        this.quoteLoaded.set(false);
        this.progressSpinnerService.hide();
        if (error.status === 401) {
          this.accountService.initAuth();
        } else if (error.status === 404) {
          this.router.navigate(['/unprotected/error']).then();
        }
      },
    });
  }

  getRequest(id: number, endUseId: number): void {
    this.isLoadingRequest.set(true);
    this.progressSpinnerService.show();
    this.httpClient.get<RequestDetails>(
      `${environment.middlewareUrl}${this.PATH_V2}/${id}?endUseId=${endUseId}`,
    ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        {
          next: (data) => {
            if (data) {
              this.request.set(data);
              this.isLoadingRequest.set(false);
              this.progressSpinnerService.hide();
            }
          },
          error: () => {
            this.isLoadingRequest.set(false);
            this.progressSpinnerService.hide();
          },
        });
  }

  getRequests(limit: number, offset: number): void {
    this.progressSpinnerService.show();
    this.requestsLoaded.set(false);
    this.httpClient.get<any>(`${environment.middlewareUrl}${this.PATH}?limit=${limit}&offset=${offset}`)
      .subscribe({
        next: (data) => {
          this.requests.set(RequestsDto(data));
          this.requestsLoaded.set(true);
          this.progressSpinnerService.hide();
        },
      });
  }

  getRequestsV2(limit: number, offset: number): void {
    this.progressSpinnerService.show();
    this.requestsV2Loaded.set(false);
    this.httpClient.get<RequestsResponseV2>(`${environment.middlewareUrl}${this.PATH_V2}?limit=${limit}&offset=${offset}`)
      .subscribe({
        next: (data) => {
          this.requestsV2.set(RequestsDtoV2(data));
          this.requestsV2Loaded.set(true);
          this.progressSpinnerService.hide();
        },
      });
  }

  getExpiringProposals(): void {
    this.httpClient.get<RequestsResponse>(
      `${environment.middlewareUrl}/request/expiring`,
    ).subscribe({
      next: (data) => {
        this.expiringProposals.set(data);
      },
    });
  }

  loadNewProposal(): void {
    this.httpClient.get<RequestsResponse>(
      `${environment.middlewareUrl}/request/proposal`,
    ).subscribe({
      next: (data) => {
        this.newProposals.set(data);
      },
    });
  }

  getExpiringProposalsV2(): void {
    this.httpClient.get<{count:number}>(
      `${environment.middlewareUrl}${this.PATH_V2}/expiring`,
    ).subscribe({
      next: (data) => {
        this.expiringProposalsCount.set(data.count);
      },
    });
  }

  loadNewProposalV2(): void {
    this.httpClient.get<NewProposalNotification>(
      `${environment.middlewareUrl}${this.PATH_V2}/proposals`,
    ).subscribe({
      next: (data) => {
        this.newProposalsV2.set(data);
      },
    });
  }

  submitFeedback(feedback: ProductFeedback, requestId: number, endUseId: number): void {
    this.progressSpinnerService.show();
    this.httpClient.post<any>(
      `${environment.middlewareUrl}${this.PATH_V2}/${requestId}/feedback`,
      feedback
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.progressSpinnerService.hide();
          this.getRequest(requestId, endUseId);
        },
        error: () => {
          this.showSnackBar('error', 'An error occurred while submitting your feedback.');
          this.progressSpinnerService.hide();
        }
      })
  }

  showSnackBar(type: string, text: string): void {
    const params = {
      type,
      text,
      icon: 'info',
      closeButton: true,
    };
    this.snackBar.openFromComponent(AlertComponent, {
      data: params,
      duration: 3000,
      verticalPosition: 'bottom',
      horizontalPosition: 'center',
      panelClass: 'custom-position'
    });
  }
}
